import * as React from 'react';

import EnrollmentChoiceModalForMultiprogram from 'bundles/enterprise-legacy-learner-home/components/EnrollmentChoiceModalForMultiprogram';
import EnrollmentChoiceModalPlaceholder from 'bundles/enterprise-legacy-learner-home/components/EnrollmentChoiceModalPlaceholder';
import { WAIT_BEFORE_ENROLLING } from 'bundles/enterprise-legacy-learner-home/constants/ProgramMiniModal';
import { useGetBestProgramToEnroll } from 'bundles/enterprise-legacy-learner-home/queryHooks/useGetBestProgramToEnroll';
import { IdTypes, createFeatureScope } from 'bundles/product-features';

import _t from 'i18n!nls/enterprise-legacy-learner-home';

const { useProductFeatures } = createFeatureScope({
  enterprise: {
    enableProgramAutoJoin: false,
  },
});

type ProgramAutoJoinBestProgramEnrollmentProps = {
  onClose: () => void;
  onSelect: (programId: string) => void;
  productId: string;
  productType: string;
  programIds: string[];
  programJoinTrackingVerbiage: boolean;
  thirdPartyOrgId: string;
  thirdPartyOrgName: string;
  userId: number;
  enableProgramAutoJoin: boolean;
};

/**
 * Program-home mini modal when auto-join is on: the modal only opens with 2+ programs, so we always
 * call getBestProgram, then auto-enroll when the API returns a valid id in programIds; otherwise manual chooser.
 */
const ProgramAutoJoinBestProgramEnrollment = ({
  onClose,
  onSelect,
  productId,
  productType,
  programIds,
  programJoinTrackingVerbiage,
  thirdPartyOrgId,
  thirdPartyOrgName,
  userId,
  enableProgramAutoJoin,
}: ProgramAutoJoinBestProgramEnrollmentProps) => {
  const [isSelectedProgramDataReady, setIsSelectedProgramDataReady] = React.useState(false);

  const { bestProgram, loading, error } = useGetBestProgramToEnroll({
    productId,
    productType,
    organizationId: thirdPartyOrgId,
  });

  const bestProgramIdInList =
    bestProgram != null && programIds.includes(bestProgram.selectedProgramId) ? bestProgram.selectedProgramId : null;

  const hasValidBestProgramFromApi = !loading && !error && bestProgramIdInList != null;

  const autoEnrollAttemptedRef = React.useRef(false);
  const autoEnrollTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    return () => {
      if (autoEnrollTimeoutRef.current != null) {
        clearTimeout(autoEnrollTimeoutRef.current);
        autoEnrollTimeoutRef.current = null;
      }
    };
  }, []);

  React.useEffect(() => {
    if (!enableProgramAutoJoin) {
      return;
    }
    if (autoEnrollAttemptedRef.current) {
      return;
    }

    const autoEnrollProgramId = bestProgramIdInList;
    if (autoEnrollProgramId == null) {
      return;
    }
    if (!isSelectedProgramDataReady) {
      return;
    }

    autoEnrollAttemptedRef.current = true;
    // Keep the selected program visible briefly before modal close.
    autoEnrollTimeoutRef.current = setTimeout(() => {
      autoEnrollTimeoutRef.current = null;
      onSelect(autoEnrollProgramId);
    }, WAIT_BEFORE_ENROLLING);
  }, [enableProgramAutoJoin, bestProgramIdInList, isSelectedProgramDataReady, onSelect]);

  React.useEffect(() => {
    // Reset readiness whenever selected-id source changes.
    setIsSelectedProgramDataReady(false);
  }, [bestProgramIdInList]);

  // finding the best program to enroll
  if (loading) {
    return (
      <EnrollmentChoiceModalPlaceholder
        productId={productId}
        productType={productType}
        headerTitle={_t('Enroll through my organization')}
        buttonTitle={_t('Continue')}
        onClose={onClose}
      />
    );
  }

  if (hasValidBestProgramFromApi) {
    return (
      <EnrollmentChoiceModalForMultiprogram
        key={bestProgramIdInList}
        onClose={onClose}
        onSelect={onSelect}
        productId={productId}
        productType={productType}
        programIds={[bestProgramIdInList]}
        programJoinTrackingVerbiage={programJoinTrackingVerbiage}
        thirdPartyOrgName={thirdPartyOrgName}
        userId={userId}
        headerTitle={_t('Enroll through my organization')}
        buttonTitle={_t('Enrolling...')}
        isEnrolling={true}
        onOptionsReady={(readyProgramId) => {
          if (readyProgramId === bestProgramIdInList) {
            setIsSelectedProgramDataReady(true);
          }
        }}
      />
    );
  }

  // fallback to the manual chooser
  return (
    <EnrollmentChoiceModalForMultiprogram
      onClose={onClose}
      onSelect={onSelect}
      productId={productId}
      productType={productType}
      programIds={programIds}
      programJoinTrackingVerbiage={programJoinTrackingVerbiage}
      thirdPartyOrgName={thirdPartyOrgName}
      userId={userId}
      headerTitle={_t('Enrollment options')}
      buttonTitle={_t('Enroll now')}
    />
  );
};

export type ProgramMiniModalEnterpriseEnrollmentChoiceProps = {
  onSelect: (programId: string) => void;
  productId: string;
  productType: string;
  programIds: string[];
  onClose: () => void;
  programJoinTrackingVerbiage: boolean;
  thirdPartyOrgName: string;
  userId: number;
  thirdPartyOrgId?: string;
};

const ProgramMiniModalEnterpriseEnrollmentChoice = (props: ProgramMiniModalEnterpriseEnrollmentChoiceProps) => {
  const {
    programIds,
    thirdPartyOrgId,
    onClose,
    onSelect,
    productId,
    productType,
    programJoinTrackingVerbiage,
    thirdPartyOrgName,
    userId,
  } = props;

  const { features } = useProductFeatures({
    idType: IdTypes.Organization,
    id: thirdPartyOrgId ?? '',
    skip: !thirdPartyOrgId,
  });
  const enableProgramAutoJoin = features.get('enterprise', 'enableProgramAutoJoin');

  if (enableProgramAutoJoin && thirdPartyOrgId) {
    return (
      <ProgramAutoJoinBestProgramEnrollment
        onClose={onClose}
        onSelect={onSelect}
        productId={productId}
        productType={productType}
        programIds={programIds}
        programJoinTrackingVerbiage={programJoinTrackingVerbiage}
        thirdPartyOrgId={thirdPartyOrgId}
        thirdPartyOrgName={thirdPartyOrgName}
        userId={userId}
        enableProgramAutoJoin={enableProgramAutoJoin}
      />
    );
  }

  return (
    <EnrollmentChoiceModalForMultiprogram
      onClose={onClose}
      onSelect={onSelect}
      productId={productId}
      productType={productType}
      programIds={programIds}
      programJoinTrackingVerbiage={programJoinTrackingVerbiage}
      thirdPartyOrgName={thirdPartyOrgName}
      userId={userId}
      headerTitle={_t('Enrollment options')}
      buttonTitle={_t('Enroll now')}
    />
  );
};

export default ProgramMiniModalEnterpriseEnrollmentChoice;
